import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowLeft,
  ArrowRight,
  ArrowsInSimple,
  ArrowSquareOut,
  ArrowUp,
  CaretLeft,
  CaretRight,
  CornersOut,
  DownloadSimple,
  File as FileIcon,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Pause,
  Play,
  SpeakerSimpleHigh,
  SpeakerSimpleSlash,
} from "@phosphor-icons/react";
import {
  Flex,
  IconButton,
  Slider,
  Spinner,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getDocument, type RenderTask } from "pdfjs-dist";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { ImageCommentPin } from "./ImageCommentPin";

import { createComment } from "../../../services/projects";
import { renderReviewCommentAvatar } from "../../../utils/helpers/ai-persona-avatars";
import { formatTimeStamp } from "../../tickets/components/VideoPlayback";
import { useHlsPlayer } from "../hooks/useHlsPlayer";

export interface AssetFile {
  source: string;
  name?: string;
  contentType?: string;
}

interface HighlightPosition {
  x: number;
  y: number;
  page?: number;
}

interface AssetViewerPaneProps {
  title: string;
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  reviewStatus?: string | null;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onPinClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (
    pageNumber: number,
    position?: { x: number; y: number },
  ) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  onCollapse?: () => void;
  onBackToProject?: () => void;
  ticketId?: string;
}

export interface AssetViewerPaneHandle {
  navigateToPage: (page: number) => void;
  navigateToFile: (fileIndex: number) => void;
}

function isPdf(asset: AssetFile): boolean {
  if (asset.contentType?.includes("pdf")) return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(pdf|ai)(\?|$)/.test(ext);
}

function isImage(asset: AssetFile): boolean {
  if (asset.contentType?.startsWith("image/")) return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/.test(ext);
}

function isOfficeFile(asset: AssetFile): boolean {
  const ext = (asset.name ?? asset.source).toLowerCase();
  if (/\.(ppt|pptx|doc|docx|xls|xlsx)(\?|$)/.test(ext)) return true;
  const ct = asset.contentType ?? "";
  return (
    ct.includes("presentationml") ||
    ct.includes("wordprocessingml") ||
    ct.includes("spreadsheetml") ||
    ct.includes("msword") ||
    ct.includes("ms-powerpoint") ||
    ct.includes("ms-excel")
  );
}

function isVideo(asset: AssetFile): boolean {
  if (asset.contentType?.startsWith("video/")) return true;
  if (
    asset.contentType === "application/vnd.apple.mpegurl" ||
    asset.contentType === "application/x-mpegurl"
  )
    return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(mp4|mov|avi|wmv|flv|mkv|m3u8)(\?|$)/.test(ext);
}

// ---------------------------------------------------------------------------
// CommentPanel -- single-comment navigation view (replaces CommentStrip)
// ---------------------------------------------------------------------------

function CommentPanel({
  comments,
  activeCommentId,
  onCommentClick,
  onCitationClick,
  onNavigateToPage,
  ticketId,
}: {
  comments: BaseTicketComment[];
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (
    pageNumber: number,
    position?: { x: number; y: number },
  ) => void;
  onNavigateToPage?: (page: number) => void;
  ticketId?: string;
}) {
  const [replyText, setReplyText] = useState("");
  const queryClient = useQueryClient();
  const replyMutation = useMutation({
    mutationFn: createComment,
    onSuccess: () => {
      setReplyText("");
      if (ticketId) {
        queryClient.invalidateQueries({
          queryKey: ["ticket-view-comments", ticketId],
        });
      }
    },
  });

  // Track index internally so the 3-second highlight auto-clear doesn't
  // reset the panel back to comment 0.
  const [internalIndex, setInternalIndex] = useState(0);

  useEffect(() => {
    if (!activeCommentId) return;
    const idx = comments.findIndex((c) => c._id === activeCommentId);
    if (idx >= 0) setInternalIndex(idx);
  }, [activeCommentId, comments]);

  const currentIndex = internalIndex;
  const comment = comments[currentIndex];
  if (!comment) return null;

  const fileCitations = (
    Array.isArray(comment.citations) ? comment.citations : []
  ).filter(
    (
      c,
    ): c is {
      url: string;
      pageNumber?: number;
      originalURL?: string;
      rel_x?: number;
      rel_y?: number;
    } => "originalURL" in c || "rel_x" in c,
  );

  const navigate = (dir: 1 | -1) => {
    const nextIdx = currentIndex + dir;
    const next = comments[nextIdx];
    if (next) {
      setInternalIndex(nextIdx);
      onCommentClick?.(next);
      if (next.page_num != null) {
        onNavigateToPage?.(next.page_num);
      }
    }
  };

  const handleReply = () => {
    const text = replyText.trim();
    if (!text || !ticketId || !comment._id) return;
    replyMutation.mutate({
      ticketId,
      payload: {
        description: text,
        commentId: comment._id,
        x: comment.x ?? comment.rel_x ?? 0,
        y: comment.y ?? comment.rel_y ?? 0,
        boardId: (comment as any).boardId,
      },
    });
  };

  return (
    <div
      data-testid="asset-viewer-comment-panel"
      className="shrink-0 rounded-t-2xl border-t border-puntt-neutral-gray-5 bg-base-white/90 backdrop-blur-sm"
    >
      {/* Navigation header */}
      <Flex
        align="center"
        justify="between"
        className="border-b border-puntt-neutral-gray-5 px-4 py-2"
      >
        <Text
          size="1"
          weight="medium"
          data-testid="comment-panel-counter"
          className="uppercase tracking-wide text-puntt-neutral-gray-9"
        >
          {currentIndex + 1}/{comments.length} Comments
          {comment.page_num != null && (
            <span className="ml-1 normal-case text-puntt-neutral-gray-8">
              · p. {comment.page_num}
            </span>
          )}
        </Text>
        <Flex gap="1">
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled={currentIndex === 0}
            onClick={() => navigate(-1)}
            aria-label="Previous comment"
            data-testid="comment-panel-prev"
          >
            <ArrowLeft size={14} />
          </IconButton>
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled={currentIndex === comments.length - 1}
            onClick={() => navigate(1)}
            aria-label="Next comment"
            data-testid="comment-panel-next"
          >
            <ArrowRight size={14} />
          </IconButton>
        </Flex>
      </Flex>

      {/* Comment card */}
      <div className="space-y-3 p-4">
        <Flex gap="2" align="center">
          {renderReviewCommentAvatar(comment)}
          <Text size="3" weight="medium" className="text-puntt-neutral-gray-12">
            {comment.rule ?? "Comment"}
          </Text>
        </Flex>

        <Text
          as="div"
          size="2"
          data-testid="comment-panel-body"
          className="leading-relaxed text-puntt-neutral-gray-12"
        >
          {comment.description}
        </Text>

        {fileCitations.length > 0 && (
          <Flex gap="2" wrap="wrap">
            {fileCitations.map((citation, idx) => {
              const page = citation.pageNumber ?? 1;
              const pos =
                citation.rel_x != null && citation.rel_y != null
                  ? { x: citation.rel_x, y: citation.rel_y }
                  : undefined;
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid="comment-panel-citation"
                  onClick={() => onCitationClick?.(page, pos)}
                  className="inline-flex cursor-pointer items-center gap-1 text-puntt-accent-11 underline-offset-2 hover:underline"
                  aria-label={`Navigate to source on page ${page}`}
                >
                  <Text as="span" size="1" weight="medium">
                    Source (p. {page})
                  </Text>
                  <ArrowSquareOut size={12} />
                </button>
              );
            })}
          </Flex>
        )}

        {ticketId && (
          <Flex gap="2" align="center">
            <TextField.Root
              size="1"
              variant="surface"
              color="gray"
              placeholder="Reply or Ask @Puntt"
              className="flex-1"
              data-testid="comment-panel-reply-input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleReply();
                }
              }}
              disabled={replyMutation.isPending}
            />
            <IconButton
              size="1"
              variant="solid"
              color="blue"
              disabled={!replyText.trim() || replyMutation.isPending}
              onClick={handleReply}
              aria-label="Send reply"
              data-testid="comment-panel-reply-send"
              className="shrink-0"
            >
              <ArrowUp size={12} weight="bold" />
            </IconButton>
          </Flex>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Office file viewer via Microsoft public embed
// ---------------------------------------------------------------------------

function OfficeViewer({ url }: { url: string }) {
  return (
    <iframe
      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
      className="size-full border-0"
      title="Office document preview"
    />
  );
}

// ---------------------------------------------------------------------------
// VideoViewer -- video player with HLS, seek bar, and comment timestamp pins
// ---------------------------------------------------------------------------

function VideoViewer({
  url,
  comments,
  activeCommentId,
  onPinClick,
}: {
  url: string;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { isLoading, error, duration } = useHlsPlayer(videoRef, url);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);

  const videoComments = useMemo(
    () => (comments ?? []).filter((c) => c.videoStart != null),
    [comments],
  );

  // Set initial volume on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seek to active comment's timestamp
  useEffect(() => {
    if (!activeCommentId || !videoRef.current) return;
    const comment = videoComments.find((c) => c._id === activeCommentId);
    if (comment?.videoStart != null) {
      videoRef.current.currentTime = comment.videoStart;
      videoRef.current.pause();
      setIsPlaying(false);
      setProgress(duration > 0 ? (comment.videoStart / duration) * 100 : 0);
    }
  }, [activeCommentId, videoComments, duration]);

  // Pause on tab switch / visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  // Pause on URL change (asset switch)
  useEffect(() => {
    return () => {
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    };
  }, [url]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const pct = (video.currentTime / video.duration) * 100;
    setProgress(pct);
    if (pct >= 100) setIsPlaying(false);
  };

  const handleSeek = (value: number[]) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const seekTime = (value[0] / 100) * video.duration;
    video.currentTime = seekTime;
    setProgress(value[0]);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
    if (videoRef.current) {
      videoRef.current.volume = value[0];
      setIsMuted(value[0] === 0);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
        <FileIcon size={32} className="text-puntt-neutral-gray-9" />
        <Text size="2" className="text-puntt-neutral-gray-11">
          Video preview unavailable
        </Text>
        <a
          href={url}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="text-white inline-flex items-center gap-1 rounded-md bg-puntt-accent-9 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-puntt-accent-10"
        >
          <DownloadSimple size={14} />
          Download
        </a>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Video area */}
      <div
        role="button"
        tabIndex={0}
        className="relative flex min-h-0 flex-1 cursor-pointer items-center justify-center bg-puntt-neutral-gray-3"
        onClick={togglePlayPause}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            togglePlayPause();
          }
        }}
      >
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-puntt-neutral-gray-3/80">
            <Spinner size="3" />
          </div>
        )}
        {/* eslint-disable jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          className="max-h-full max-w-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          playsInline
        />
        {!isPlaying && !isLoading && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-puntt-neutral-gray-10/75 p-3">
            <Play className="text-white" size={28} weight="fill" />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="shrink-0 border-t border-puntt-neutral-gray-5 bg-base-white">
        {/* Seek bar with comment pins */}
        <div className="relative px-3 pt-2">
          <Slider
            variant="classic"
            size="1"
            min={0}
            max={100}
            step={0.1}
            value={[progress]}
            onValueChange={handleSeek}
            className="z-[1] cursor-pointer [--cursor-slider-thumb:pointer] [--radius-factor:0]"
          />
          {/* Comment pins overlay */}
          <div className="pointer-events-none absolute inset-x-3 top-0 h-5">
            {videoComments.map((comment) => {
              const left =
                duration > 0 ? ((comment.videoStart ?? 0) / duration) * 100 : 0;
              return (
                <button
                  key={comment._id}
                  className={`pointer-events-auto absolute top-0 z-10 size-2.5 -translate-x-1/2 rounded-full transition-transform ${
                    comment._id === activeCommentId
                      ? "scale-150 bg-puntt-accent-9"
                      : "bg-puntt-gold-9 hover:scale-125"
                  }`}
                  style={{ left: `${left}%` }}
                  title={`${formatTimeStamp(comment.videoStart)} — ${comment.description?.slice(0, 60) ?? ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPinClick?.(comment);
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Transport controls */}
        <Flex align="center" justify="between" className="px-3 py-1.5">
          <Flex align="center" gap="2">
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              onClick={togglePlayPause}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </IconButton>
            <Text size="1" className="tabular-nums text-puntt-neutral-gray-11">
              {formatTimeStamp(videoRef.current?.currentTime ?? 0)} /{" "}
              {formatTimeStamp(duration)}
            </Text>
          </Flex>
          <Flex align="center" gap="2">
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <SpeakerSimpleSlash size={16} />
              ) : (
                <SpeakerSimpleHigh size={16} />
              )}
            </IconButton>
            <Slider
              variant="classic"
              size="1"
              min={0}
              max={1}
              step={0.05}
              value={[isMuted ? 0 : volume]}
              onValueChange={handleVolumeChange}
              className="w-16 cursor-pointer [--cursor-slider-thumb:pointer]"
            />
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              onClick={toggleFullscreen}
              aria-label="Fullscreen"
            >
              <CornersOut size={16} />
            </IconButton>
          </Flex>
        </Flex>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PdfViewer -- full-pane renderer with page nav, highlight overlay, and pins
// ---------------------------------------------------------------------------

function PdfViewer({
  url,
  highlightPosition,
  onPageChange,
  comments,
  activeCommentId,
  onPinClick,
  onPinDragEnd,
}: {
  url: string;
  highlightPosition?: HighlightPosition | null;
  onPageChange?: (setter: (page: number) => void) => void;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const pdfDocRef = useRef<Awaited<
    ReturnType<typeof getDocument>["promise"]
  > | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const renderGenRef = useRef(0);
  const totalPagesRef = useRef(0);
  totalPagesRef.current = totalPages;

  useEffect(() => {
    onPageChange?.((page: number) => {
      if (page >= 1 && page <= totalPagesRef.current) {
        setCurrentPage(page);
      }
    });
  }, [onPageChange]);

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const renderPage = useCallback(async (pageNum: number) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    // PDF.js cannot render two pages into the same canvas at once. Cancel the
    // in-flight render; track generation so a superseded call can no-op cleanly.
    const myGen = ++renderGenRef.current;
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch {
        // cancel() may throw if the task already settled; safe to ignore.
      }
      renderTaskRef.current = null;
    }

    setRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      if (myGen !== renderGenRef.current) return;

      const scale = 1.5 * zoomRef.current;
      const viewport = page.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext("2d");
      if (!context) {
        setError("Failed to render page");
        setRendering(false);
        return;
      }

      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;
      await task.promise;
      if (myGen === renderGenRef.current) {
        renderTaskRef.current = null;
        setRendering(false);
      }
    } catch (err) {
      // A superseded render rejects with RenderingCancelledException; expected.
      if (
        (err as { name?: string } | null)?.name ===
        "RenderingCancelledException"
      ) {
        return;
      }
      console.error("Error rendering PDF page:", err);
      if (myGen === renderGenRef.current) {
        setError("Failed to render page");
        setRendering(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      try {
        const pdfDoc = await getDocument(url).promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setTotalPages(pdfDoc.numPages);
        await renderPage(1);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading PDF:", err);
        setError("Failed to load PDF");
        setRendering(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
      // Bump generation so any renderPage still suspended in getPage() short-
      // circuits after resume instead of touching a detached canvas/state.
      renderGenRef.current++;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
        renderTaskRef.current = null;
      }
    };
  }, [url, renderPage]);

  useEffect(() => {
    if (pdfDocRef.current && currentPage > 0) {
      renderPage(currentPage);
    }
  }, [currentPage, zoom, renderPage]);

  useEffect(() => {
    if (highlightPosition?.page && highlightPosition.page !== currentPage) {
      setCurrentPage(highlightPosition.page);
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [url]);

  const pageComments = (comments ?? []).filter(
    (c) => (c.page_num ?? 1) === currentPage,
  );

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <Text size="2" className="text-puntt-neutral-gray-9">
          {error}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-auto bg-puntt-neutral-gray-3 p-2"
      >
        {rendering && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-puntt-neutral-gray-3/80">
            <Spinner size="2" />
          </div>
        )}
        <div
          ref={wrapperRef}
          className="relative mx-auto"
          style={{ width: `${zoom * 100}%` }}
        >
          <canvas ref={canvasRef} className="block w-full" />
          {pageComments.map((comment) => (
            <ImageCommentPin
              key={comment._id}
              comment={comment}
              isActive={comment._id === activeCommentId}
              onPinClick={(c) => onPinClick?.(c)}
              onDragEnd={onPinDragEnd}
              containerRef={wrapperRef}
            />
          ))}
          {highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentPage) && (
              <div
                className="pointer-events-none absolute z-20 size-7 -translate-x-1/2 -translate-y-full animate-ping rounded-full border-2 border-puntt-accent-9 bg-puntt-accent-9/30"
                style={{
                  left: `${highlightPosition.x * 100}%`,
                  top: `${highlightPosition.y * 100}%`,
                }}
              />
            )}
        </div>
      </div>

      <ViewerToolbar
        currentPage={currentPage}
        totalPages={totalPages}
        zoom={zoom}
        onPageChange={setCurrentPage}
        onZoomChange={setZoom}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImageViewer -- image gallery with highlight overlay, comment pins, and zoom
// ---------------------------------------------------------------------------

function ImageViewer({
  assets,
  highlightPosition,
  comments,
  activeCommentId,
  onPinClick,
  onPinDragEnd,
  onPageChange,
}: {
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  onPageChange?: (setter: (page: number) => void) => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const totalPages = assets.length;
  const asset = assets[currentIndex] ?? assets[0];
  const totalPagesRef = useRef(totalPages);
  totalPagesRef.current = totalPages;

  useEffect(() => {
    onPageChange?.((page: number) => {
      if (page >= 1 && page <= totalPagesRef.current) {
        setCurrentIndex(page - 1);
      }
    });
  }, [onPageChange]);

  useEffect(() => {
    if (
      highlightPosition?.page &&
      highlightPosition.page !== currentIndex + 1
    ) {
      setCurrentIndex(
        Math.min(Math.max(highlightPosition.page - 1, 0), totalPages - 1),
      );
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [assets, currentIndex]);

  const pageComments = (comments ?? []).filter(
    (c) => (c.page_num ?? 1) === currentIndex + 1,
  );

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-auto bg-puntt-neutral-gray-3 p-2"
      >
        <div
          ref={wrapperRef}
          className="relative mx-auto"
          style={{ width: `${zoom * 100}%` }}
        >
          <img
            src={asset.source}
            alt={asset.name ?? `Image ${currentIndex + 1}`}
            className="block w-full"
          />
          {pageComments.map((comment) => (
            <ImageCommentPin
              key={comment._id}
              comment={comment}
              isActive={comment._id === activeCommentId}
              onPinClick={(c) => onPinClick?.(c)}
              onDragEnd={onPinDragEnd}
              containerRef={wrapperRef}
            />
          ))}
          {highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentIndex + 1) && (
              <div
                className="pointer-events-none absolute z-20 size-7 -translate-x-1/2 -translate-y-full animate-ping rounded-full border-2 border-puntt-accent-9 bg-puntt-accent-9/30"
                style={{
                  left: `${highlightPosition.x * 100}%`,
                  top: `${highlightPosition.y * 100}%`,
                }}
              />
            )}
        </div>
      </div>

      <ViewerToolbar
        currentPage={currentIndex + 1}
        totalPages={totalPages}
        zoom={zoom}
        onPageChange={(page) => setCurrentIndex(page - 1)}
        onZoomChange={setZoom}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared toolbar for pagination + zoom (DRYs up PdfViewer / ImageViewer)
// ---------------------------------------------------------------------------

function ViewerToolbar({
  currentPage,
  totalPages,
  zoom,
  onPageChange,
  onZoomChange,
}: {
  currentPage: number;
  totalPages: number;
  zoom: number;
  onPageChange: (page: number) => void;
  onZoomChange: (zoom: number | ((z: number) => number)) => void;
}) {
  return (
    <Flex
      align="center"
      justify="between"
      className="shrink-0 border-t border-puntt-neutral-gray-5 bg-base-white px-3 py-2"
    >
      {totalPages > 1 ? (
        <Flex align="center" gap="2">
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            data-testid="asset-viewer-prev-page"
            aria-label="Previous page"
          >
            <CaretLeft size={14} />
          </IconButton>
          <Text
            size="1"
            className="tabular-nums text-puntt-neutral-gray-11"
            data-testid="asset-viewer-page-indicator"
          >
            Page {currentPage} of {totalPages}
          </Text>
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            data-testid="asset-viewer-next-page"
            aria-label="Next page"
          >
            <CaretRight size={14} />
          </IconButton>
        </Flex>
      ) : (
        <span />
      )}
      <Flex align="center" gap="1">
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          disabled={zoom <= 0.5}
          onClick={() => onZoomChange((z: number) => Math.max(0.5, z - 0.25))}
          aria-label="Zoom out"
          data-testid="asset-viewer-zoom-out"
        >
          <MagnifyingGlassMinus size={14} />
        </IconButton>
        <button
          onClick={() => onZoomChange(1)}
          className="min-w-12 rounded px-1.5 py-0.5 text-xs tabular-nums text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3"
          aria-label="Reset zoom"
          data-testid="asset-viewer-zoom-reset"
        >
          {Math.round(zoom * 100)}%
        </button>
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          disabled={zoom >= 3}
          onClick={() => onZoomChange((z: number) => Math.min(3, z + 0.25))}
          aria-label="Zoom in"
          data-testid="asset-viewer-zoom-in"
        >
          <MagnifyingGlassPlus size={14} />
        </IconButton>
      </Flex>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// AssetViewerPane -- main exported component
// ---------------------------------------------------------------------------

export const AssetViewerPane = forwardRef<
  AssetViewerPaneHandle,
  AssetViewerPaneProps
>(function AssetViewerPane(
  {
    title,
    assets,
    highlightPosition,
    reviewStatus,
    comments,
    activeCommentId,
    onCommentClick,
    onPinClick,
    onCitationClick,
    onPinDragEnd,
    onCollapse,
    onBackToProject,
    ticketId,
  },
  ref,
) {
  const [activeTab, setActiveTab] = useState(0);
  const pageNavigatorRef = useRef<((page: number) => void) | null>(null);

  useImperativeHandle(ref, () => ({
    navigateToPage(page: number) {
      pageNavigatorRef.current?.(page);
    },
    navigateToFile(fileIndex: number) {
      if (fileIndex >= 0 && fileIndex < assets.length) {
        setActiveTab(fileIndex);
      }
    },
  }));

  useEffect(() => {
    if (!activeCommentId || !comments?.length) return;
    const comment = comments.find((c) => c._id === activeCommentId);
    if (comment?.page_num != null && pageNavigatorRef.current) {
      pageNavigatorRef.current(comment.page_num);
    }
  }, [activeCommentId, comments]);

  if (assets.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          No {title.toLowerCase()} files
        </Text>
      </div>
    );
  }

  const currentAsset = assets[activeTab] ?? assets[0];
  const allImages = assets.length > 1 && assets.every((a) => isImage(a));
  const showTabs = assets.length > 1 && !allImages;
  const hasComments = comments && comments.length > 0;

  return (
    <div
      data-testid="asset-viewer-pane"
      className="flex h-full flex-col bg-base-white"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-puntt-neutral-gray-5 px-4 py-2">
        <Flex align="center" gap="2" className={showTabs ? "mb-1" : ""}>
          {onBackToProject && (
            <button
              onClick={onBackToProject}
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-puntt-accent-9 transition-colors hover:bg-puntt-accent-2"
              aria-label="Back to project"
              data-testid="back-to-project"
            >
              <ArrowLeft size={12} weight="bold" />
              <Text size="1" weight="medium">
                Project
              </Text>
            </button>
          )}
          <Text
            size="1"
            weight="medium"
            className="uppercase tracking-wide text-puntt-neutral-gray-9"
          >
            {title}
          </Text>
          {onCollapse && (
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              className="ml-auto"
              aria-label={`Collapse ${title} pane`}
              data-testid="asset-viewer-collapse"
              onClick={onCollapse}
            >
              <ArrowsInSimple size={14} />
            </IconButton>
          )}
        </Flex>
        {reviewStatus && (
          <Text
            size="1"
            className={`italic ${
              reviewStatus === "Review complete"
                ? "text-malachite-700"
                : "animate-pulse text-puntt-accent-9"
            }`}
          >
            {reviewStatus}
          </Text>
        )}
        {showTabs && (
          <Flex gap="1" className="overflow-x-auto">
            {assets.map((asset, i) => (
              <button
                key={i}
                data-testid="asset-viewer-tab"
                onClick={() => setActiveTab(i)}
                className={`rounded px-2 py-0.5 transition-colors ${
                  i === activeTab
                    ? "bg-puntt-accent-3 text-puntt-accent-11"
                    : "text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-3"
                }`}
              >
                <Text
                  as="span"
                  size="1"
                  weight={i === activeTab ? "medium" : undefined}
                >
                  {asset.name ?? `File ${i + 1}`}
                </Text>
              </button>
            ))}
          </Flex>
        )}
      </div>

      {/* Content */}
      <div className="min-h-0 flex-1">
        {allImages ? (
          <ImageViewer
            assets={assets}
            highlightPosition={highlightPosition}
            onPageChange={(setter) => {
              pageNavigatorRef.current = setter;
            }}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={onPinDragEnd}
          />
        ) : isPdf(currentAsset) ? (
          <PdfViewer
            key={currentAsset.source}
            url={currentAsset.source}
            highlightPosition={highlightPosition}
            onPageChange={(setter) => {
              pageNavigatorRef.current = setter;
            }}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={onPinDragEnd}
          />
        ) : isImage(currentAsset) ? (
          <ImageViewer
            assets={[currentAsset]}
            highlightPosition={highlightPosition}
            onPageChange={(setter) => {
              pageNavigatorRef.current = setter;
            }}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={onPinDragEnd}
          />
        ) : isVideo(currentAsset) ? (
          <VideoViewer
            url={currentAsset.source}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
          />
        ) : isOfficeFile(currentAsset) ? (
          <OfficeViewer url={currentAsset.source} />
        ) : (
          <ImageViewer
            assets={[currentAsset]}
            highlightPosition={highlightPosition}
            onPageChange={(setter) => {
              pageNavigatorRef.current = setter;
            }}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={onPinDragEnd}
          />
        )}
      </div>

      {/* Comment panel */}
      {hasComments && (
        <CommentPanel
          comments={comments}
          activeCommentId={activeCommentId}
          onCommentClick={onCommentClick}
          onCitationClick={onCitationClick}
          onNavigateToPage={(page) => pageNavigatorRef.current?.(page)}
          ticketId={ticketId}
        />
      )}
    </div>
  );
});
